import { useCallback, useEffect, useState } from 'react';

/**
 * Tracks which embedded tables are collapsed, one entry per (data row, column group).
 *
 * Keys address rows by DATA row index rather than by position among the visible rows: the index
 * survives `hideRow` filtering and search, and it is the same index field paths are built from, so
 * a key maps straight onto `<dataPrefix>.<index>.<parentColumnPath>`.
 *
 * Annotation-driven expansion is deliberately NOT subscribed to here — see `FocusedGroupExpander`,
 * which owns that subscription so the table does not re-render on every navigation.
 *
 * @param data - The table's rows. A change in their number clears the state, because adding or
 * removing a row shifts every following index and a surviving key would address a different line
 * item.
 */
export const useCollapsedGroups = ({ data }: { data: Array<unknown> | undefined }) => {
  const [collapsedGroups, setCollapsedGroups] = useState<ReadonlySet<string>>(() => new Set<string>());
  const dataRowCount = data?.length ?? 0;

  useEffect(() => {
    setCollapsedGroups((current) => (current.size === 0 ? current : new Set<string>()));
  }, [dataRowCount]);

  const expandGroupForField = useCallback((focusedFieldPath: string | undefined, dataPrefix: string | undefined) => {
    setCollapsedGroups((current) => withFocusedGroupExpanded(current, focusedFieldPath, dataPrefix));
  }, []);

  const toggleGroupCollapsed = useCallback((rowIndex: number, parentColumnPath: string) => {
    setCollapsedGroups((current) => {
      const next = new Set(current);
      const key = collapsedGroupKey(rowIndex, parentColumnPath);
      if (!next.delete(key)) {
        next.add(key);
      }
      return next;
    });
  }, []);

  const isGroupCollapsed = useCallback(
    (rowIndex: number, parentColumnPath: string) => collapsedGroups.has(collapsedGroupKey(rowIndex, parentColumnPath)),
    [collapsedGroups],
  );

  /**
   * Expands every collapsed group of one data row.
   *
   * Kept alongside the `focusedFieldPath` effect above, which covers navigation that sets a current
   * item — every `moveTo*` entry point does. It does not cover `focusOnCurrentItem` called directly
   * with no cursor yet (`DiscrepanciesBanner`'s button): that falls back to `items[0]` WITHOUT
   * making it current, so `useCurrentItem()` never changes and the effect never fires.
   */
  const expandRow = useCallback((rowIndex: number) => {
    setCollapsedGroups((current) => {
      const prefix = collapsedGroupKey(rowIndex, '');
      const next = new Set(Array.from(current).filter((key) => !key.startsWith(prefix)));
      return next.size === current.size ? current : next;
    });
  }, []);

  return { collapsedGroups, toggleGroupCollapsed, isGroupCollapsed, expandRow, expandGroupForField };
};

const collapsedGroupKey = (rowIndex: number, parentColumnPath: string) => `${rowIndex}::${parentColumnPath}`;

/**
 * Drops the group containing `focusedFieldPath` from the collapsed set, leaving every other
 * collapsed group as the user left it. Returns the same set when nothing matches, so it is safe
 * to call from a state updater on every navigation.
 */
const withFocusedGroupExpanded = (
  collapsedGroups: ReadonlySet<string>,
  focusedFieldPath: string | undefined,
  dataPrefix: string | undefined,
): ReadonlySet<string> => {
  if (!focusedFieldPath || collapsedGroups.size === 0) {
    return collapsedGroups;
  }

  const prefix = dataPrefix ? `${dataPrefix}.` : '';
  const remaining = Array.from(collapsedGroups).filter((key) => {
    const separatorIndex = key.indexOf('::');
    const rowIndex = key.slice(0, separatorIndex);
    const parentColumnPath = key.slice(separatorIndex + 2);

    return !focusedFieldPath.startsWith(`${prefix}${rowIndex}.${parentColumnPath}.`);
  });

  return remaining.length === collapsedGroups.size ? collapsedGroups : new Set(remaining);
};
